import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import './Admin.css';

const EMPTY = { title:'', description:'', location_name:'', location_address:'', event_date:'', event_time:'', end_time:'', external_url:'', is_published:true };
const IMG_BASE = 'https://runarounddogtown.com/rescue-api';

export default function AdminEvents() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [current, setCurrent] = useState(null);
  const [form,    setForm]    = useState(EMPTY);
  const [photo,   setPhoto]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.admin.getAllEvents().then(setEvents).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(EMPTY); setCurrent(null); setPhoto(null); setError(''); setModal('form'); };

  const openEdit = ev => {
    setCurrent(ev);
    setForm({
      title:            ev.title            || '',
      description:      ev.description      || '',
      location_name:    ev.location_name    || '',
      location_address: ev.location_address || '',
      event_date:       ev.event_date       || '',
      event_time:       ev.event_time       || '',
      end_time:         ev.end_time         || '',
      external_url:     ev.external_url     || '',
      is_published:     ev.is_published !== 0,
    });
    setPhoto(null); setError(''); setModal('form');
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title)      { setError('Title is required'); return; }
    if (!form.event_date) { setError('Date is required');  return; }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append('photo', photo);
      if (modal === 'add' || !current) {
        await api.admin.createEvent(fd);
      } else {
        await api.admin.updateEvent(current.id, fd);
      }
      setModal(null); load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    try { await api.admin.deleteEvent(id); setConfirmDelete(null); load(); }
    catch (err) { alert(err.message); }
  };

  const fmtDate = d => {
    if (!d) return '—';
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Events</h1>
          <p className="admin-page__subtitle">{events.length} total events</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Event</button>
      </div>

      <div className="admin-section">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : events.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty__icon">📅</div>
            <h3>No events yet</h3>
            <p>Click "Add Event" to create your first event.</p>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Photo</th><th>Title</th><th>Date</th><th>Location</th><th>Published</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <tr key={ev.id}>
                    <td>
                      {ev.photo
                        ? <img className="dog-thumb" src={`${IMG_BASE}${ev.photo}`} alt={ev.title} />
                        : <div className="dog-thumb-placeholder">📅</div>
                      }
                    </td>
                    <td><strong>{ev.title}</strong></td>
                    <td style={{ whiteSpace:'nowrap' }}>{fmtDate(ev.event_date)}</td>
                    <td>{ev.location_name || '—'}</td>
                    <td>{ev.is_published ? <span className="badge badge-available">Yes</span> : <span className="badge badge-pending">No</span>}</td>
                    <td>
                      {confirmDelete === ev.id ? (
                        <div className="confirm-delete">
                          Sure?
                          <button className="btn-icon btn-icon--danger" onClick={() => handleDelete(ev.id)}>Yes</button>
                          <button className="btn-icon" onClick={() => setConfirmDelete(null)}>No</button>
                        </div>
                      ) : (
                        <div className="table-actions">
                          <button className="btn-icon" onClick={() => openEdit(ev)}>✏️ Edit</button>
                          <button className="btn-icon btn-icon--danger" onClick={() => setConfirmDelete(ev.id)}>🗑️</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'form' && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="admin-modal">
            <div className="admin-modal__header">
              <h2>{current ? `Edit: ${current.title}` : 'Add New Event'}</h2>
              <button className="admin-modal__close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="admin-modal__body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} autoFocus />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-control" type="date" value={form.event_date} onChange={e => f('event_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <input className="form-control" type="time" value={form.event_time} onChange={e => f('event_time', e.target.value)} />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Location Name</label>
                  <input className="form-control" value={form.location_name} onChange={e => f('location_name', e.target.value)} placeholder="e.g. Petco Roswell" />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input className="form-control" value={form.location_address} onChange={e => f('location_address', e.target.value)} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={form.description} onChange={e => f('description', e.target.value)} />
              </div>

              <div className="form-group">
                <label className="form-label">External Link (Facebook event, etc.)</label>
                <input className="form-control" type="url" value={form.external_url} onChange={e => f('external_url', e.target.value)} placeholder="https://..." />
              </div>

              <div className="form-group">
                <label className="form-label">Event Photo</label>
                {current?.photo && !photo && (
                  <img src={`${IMG_BASE}${current.photo}`} alt="current" style={{ width:80, height:60, objectFit:'cover', borderRadius:'var(--radius)', marginBottom:8 }} />
                )}
                <label className="photo-upload-area">
                  <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} />
                  {photo ? `✓ ${photo.name}` : 'Click to upload event photo'}
                </label>
              </div>

              <label className="form-check">
                <input type="checkbox" checked={form.is_published} onChange={e => f('is_published', e.target.checked)} />
                Published (visible on public website)
              </label>
            </div>
            <div className="admin-modal__footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : current ? 'Save Changes' : 'Add Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
